import { FC, HTMLAttributes, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useStyles } from "./GuestPage.style";
import { login } from "@onmo/auth";
import clsx from "classnames";
import { StaticImage } from "../StaticImage";
import { Typography } from "../../core/Typography/Typography";
import {
  TypographyVariant,
} from "../../../constants/typography";
import { Icon } from "../../core/Icon/Icon";
import Menu from "../MainHeader/assets/menu_1x.webp";
import Menu2x from "../MainHeader/assets/menu_2x.webp";
import Menu3x from "../MainHeader/assets/menu_3x.webp";
import useInView from "../../../hooks/useInView";
import useResizeScreen from "../../../hooks/useResizeScreen";
import { useAppSelector } from "../../hooks";
import { assetsImages } from "../../../models/theme";
import BottomNavbar from "../BottomNavbar/BottomNavbar";
import { FEATURES } from "../../../types/theme";

const title = "Galaxy Shooters";
const video =
  "https://assets.onmostealth.com/static/gaming-platform/Vodacom/home-feed/videos/Galaxy-Shooter.mp4";
const background =
  "https://assets.onmostealth.com/static/gaming-platform/Vodacom/home-feed/gamearts/Galaxy-Shooters.png";
const icon =
  "https://assets.onmogaming.com/static/gaming-platform/Vodacom/home-feed/icons/Galaxy-Shooters.png";

export const GuestPage: FC<HTMLAttributes<HTMLDivElement>> = () => {
  const classes = useStyles();
  const { t } = useTranslation();
  const [shouldPlayVideo, setShouldPlayVideo] = useState(false);
  const [hasPlayedOnce, setHasPlayedOnce] = useState(false);
  const { ref, inView } = useInView();
  const screenHeight = useResizeScreen();
  const { theme } = useAppSelector((state) => state.theme);
  const isNavbarHidden = theme?.hideFeature?.includes(FEATURES.bottomNavbar) || false;

  const showVideo = !!(shouldPlayVideo && video);

  const onRootClick = async () => {
    await login(window.location.origin);
  };

  useEffect(() => {
    let timer: ReturnType<typeof setTimeout> | undefined;

    if (inView && video && !hasPlayedOnce) {
      timer = setTimeout(() => {
        setShouldPlayVideo(true);
      }, 1000);
    }

    return () => {
      if (timer) clearTimeout(timer);
    };
  }, [inView, video, hasPlayedOnce]);

  const handleVideoEnded = () => {
    setShouldPlayVideo(false);
    setHasPlayedOnce(true);
  };

  return (
    <div
      className={classes.root}
      onClick={onRootClick}
      style={{ height: screenHeight }}
    >
      <div className={classes.header} data-role="main-header">
        <StaticImage src={assetsImages.headerLogo} className={classes.logo} />
        <div className={classes.headerRight}>
          <StaticImage
            src={Menu}
            src2x={Menu2x}
            src3x={Menu3x}
            data-testid="menu-icon"
          />
        </div>
      </div>
      <div ref={ref} style={{ width: "100vw" }}>
        <StaticImage className={classes.featureImage} src={background} />
        {showVideo && (
          <video
            src={video}
            className={classes.video}
            style={{ height: screenHeight }}
            autoPlay
            muted
            playsInline
            preload="auto"
            onEnded={handleVideoEnded}
          />
        )}
        {!showVideo && hasPlayedOnce && video && (
          <button type="button" className={classes.playBtn}>
            <div className={classes.iconBtn}>▶</div>
          </button>
        )}
      </div>
      <div className={classes.footerOverlay}>
        <div className={clsx(classes.content,
          isNavbarHidden && classes.contentWithoutNavbar)}>
          <div className={classes.title}>
            <div className={classes.leftContent}>
              <StaticImage src={icon} className={classes.game} />
              <div className={classes.titleContent}>
                <Typography variant={TypographyVariant.BodyB3Bold}>
                  {title}
                </Typography>
                <Typography
                  variant={TypographyVariant.BodyB5Regular}
                  className={classes.freeToPlay}
                >
                  ({t("cta_Free to Play")})
                  <Icon name="icon-bold-arrow-right-3" size={20} />
                </Typography>
              </div>
            </div>
            <div className={classes.icons}>
              <Icon name="icon-download" className={classes.icon} size={32} />
              <Icon name="icon-bold-heart" className={classes.icon} size={32} />
              <Icon name="icon-bold-share" className={classes.icon} size={32} />
            </div>
          </div>
        </div>
        <BottomNavbar />
      </div>
    </div>
  );
};
